import { useEffect, useRef } from "react"
import maplibregl from "maplibre-gl"
import "maplibre-gl/dist/maplibre-gl.css"
import MapSearchBar from "./MapSearchBar"
import type { Spot } from "../types"

interface MapProps {
  onMapClick: (lat: number, lng: number) => void
  onMarkerClick: (spot: Spot) => void
  onSearch: (lat: number, lng: number, name: string) => void
  onConfirmLocation?: () => void
  onCancelLocation?: () => void
  spots: Spot[]
  center?: { lat: number; lng: number } | null
  isPlacingSpot?: boolean
  pendingCoords?: { lat: number; lng: number } | null
  confirmLabels?: { title: string; yes: string; no: string }
}

const SPAIN_CENTER: [number, number] = [-3.7, 40.4]
const DEFAULT_ZOOM = 6
const SPAIN_BOUNDS: maplibregl.LngLatBoundsLike = [
  [-20, 24],
  [10, 50],
]

const CATEGORY_MARKER_COLORS: Record<string, string> = {
  free_wild: "#2d5a3d",
  paid_campsite: "#5b8db8",
  rest_area: "#d4b896",
  service_point: "#2a6b6b",
}

export default function Map({ onMapClick, onMarkerClick, onSearch, onConfirmLocation, onCancelLocation, spots, center, isPlacingSpot, pendingCoords, confirmLabels }: MapProps) {
  const mapContainer = useRef<HTMLDivElement>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  const markersRef = useRef<maplibregl.Marker[]>([])
  const pendingMarkerRef = useRef<maplibregl.Marker | null>(null)
  const pendingPopupRef = useRef<maplibregl.Popup | null>(null)

  const onMapClickRef = useRef(onMapClick)
  onMapClickRef.current = onMapClick

  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return

    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: {
        version: 8,
        sources: {
          osm: {
            type: "raster",
            tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
            tileSize: 256,
            attribution:
              '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          },
        },
        layers: [
          {
            id: "osm",
            type: "raster",
            source: "osm",
          },
        ],
      },
      center: SPAIN_CENTER,
      zoom: DEFAULT_ZOOM,
      maxBounds: SPAIN_BOUNDS,
    })

    map.addControl(new maplibregl.NavigationControl(), "top-right")
    map.addControl(new maplibregl.GeolocateControl({ trackUserLocation: true }), "top-right")
    map.addControl(new maplibregl.ScaleControl(), "bottom-left")

    map.on("click", (e) => {
      onMapClickRef.current(e.lngLat.lat, e.lngLat.lng)
    })

    map.on("load", () => {
      map.resize()
    })

    mapRef.current = map

    const resizeObserver = new ResizeObserver(() => {
      map.resize()
    })
    resizeObserver.observe(mapContainer.current)

    return () => {
      resizeObserver.disconnect()
      map.remove()
      mapRef.current = null
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (center) {
      map.flyTo({ center: [center.lng, center.lat], zoom: 14 })
    }
  }, [center])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    markersRef.current.forEach((m) => m.remove())
    markersRef.current = []

    spots.forEach((spot) => {
      const color = CATEGORY_MARKER_COLORS[spot.category] || "#3b82f6"

      const el = document.createElement("div")
      el.className = "cursor-pointer"
      el.innerHTML = `<svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 0C7.163 0 0 7.163 0 16c0 12 16 24 16 24s16-12 16-24C32 7.163 24.837 0 16 0z" fill="${color}"/>
        <circle cx="16" cy="16" r="6" fill="white"/>
      </svg>`

      const popup = new maplibregl.Popup({ offset: 16, closeButton: false }).setHTML(
        `<div class="font-semibold text-sm">${spot.name}</div>
         <div class="text-xs text-gray-500">${spot.category}</div>
         ${spot.rating_avg !== null ? `<div class="text-xs text-yellow-500">★ ${spot.rating_avg}</div>` : ""}`
      )

      const marker = new maplibregl.Marker({ element: el, anchor: "bottom" })
        .setLngLat([spot.longitude, spot.latitude])
        .setPopup(popup)
        .addTo(map)

      el.addEventListener("click", (e) => {
        e.stopPropagation()
        onMarkerClick(spot)
      })

      markersRef.current.push(marker)
    })
  }, [spots, onMarkerClick])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    pendingMarkerRef.current?.remove()
    pendingPopupRef.current?.remove()
    pendingMarkerRef.current = null
    pendingPopupRef.current = null

    if (!pendingCoords) return

    const el = document.createElement("div")
    el.innerHTML = `<svg width="36" height="44" viewBox="0 0 36 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 0C8.059 0 0 8.059 0 18c0 14.4 18 26 18 26s18-11.6 18-26C36 8.059 27.941 0 18 0z" fill="#EF4444"/>
      <circle cx="18" cy="18" r="7" fill="white"/>
    </svg>`

    pendingMarkerRef.current = new maplibregl.Marker({ element: el, anchor: "bottom" })
      .setLngLat([pendingCoords.lng, pendingCoords.lat])
      .addTo(map)

    if (onConfirmLocation && onCancelLocation && confirmLabels) {
      const popupEl = document.createElement("div")
      popupEl.className = "bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-4 w-56 space-y-3 text-center"
      popupEl.innerHTML = `
        <div class="w-10 h-10 rounded-full bg-steel/10 dark:bg-steel/20 text-steel dark:text-steel-light flex items-center justify-center text-xl mx-auto">📍</div>
        <p class="text-sm font-semibold text-gray-900 dark:text-white">${confirmLabels.title}</p>
        <div class="flex gap-2">
          <button class="flex-1 py-2 rounded-lg bg-steel text-white text-sm font-medium hover:bg-steel-dark confirm-btn">${confirmLabels.yes}</button>
          <button class="flex-1 py-2 rounded-lg bg-gray-200 dark:bg-slate-700 text-gray-800 dark:text-gray-200 text-sm font-medium hover:bg-gray-300 dark:hover:bg-slate-600 cancel-btn">${confirmLabels.no}</button>
        </div>
      `
      popupEl.querySelector(".confirm-btn")?.addEventListener("click", onConfirmLocation)
      popupEl.querySelector(".cancel-btn")?.addEventListener("click", onCancelLocation)

      pendingPopupRef.current = new maplibregl.Popup({ offset: 48, closeButton: false, closeOnClick: false })
        .setLngLat([pendingCoords.lng, pendingCoords.lat])
        .setDOMContent(popupEl)
        .addTo(map)
    }
  }, [pendingCoords, onConfirmLocation, onCancelLocation])

  return (
    <div className="relative w-full h-full">
      <MapSearchBar onSearch={onSearch} />
      <div
        ref={mapContainer}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: "100%",
          height: "100%",
          minHeight: "200px",
          cursor: isPlacingSpot ? "crosshair" : "grab",
        }}
      />
    </div>
  )
}
